import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Camera, X, Upload, Loader2, AlertCircle, CheckCircle } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../context/AuthContext';

interface ProfilePictureUploadProps {
  currentImageUrl: string | null;
  onImageUpdate: (url: string) => void;
}

const ProfilePictureUpload: React.FC<ProfilePictureUploadProps> = ({ 
  currentImageUrl, 
  onImageUpdate 
}) => {
  const { user } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(currentImageUrl);

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (!user) {
      setError('You must be logged in to upload a profile picture');
      return;
    }

    const file = acceptedFiles[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Only image files are allowed');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setError('File size must be less than 5MB');
      return;
    }

    setUploading(true);
    setError(null);
    setSuccess(null);

    try {
      // Create a preview
      const objectUrl = URL.createObjectURL(file);
      setPreviewUrl(objectUrl);

      // Generate a unique file name
      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}-${Date.now()}.${fileExt}`;
      const filePath = `${user.id}/${fileName}`;

      // Upload to Supabase Storage
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('profile-images')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: true
        });

      if (uploadError) {
        throw uploadError;
      }

      // Get the public URL
      const { data: urlData } = supabase.storage
        .from('profile-images')
        .getPublicUrl(filePath);

      if (!urlData || !urlData.publicUrl) {
        throw new Error('Failed to get public URL for uploaded image');
      }

      // Update profile with new image URL
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ 
          profile_image_url: urlData.publicUrl,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);

      if (updateError) {
        throw updateError;
      }

      // Call the callback with the new URL
      onImageUpdate(urlData.publicUrl);
      setSuccess('Profile picture updated successfully');

      // Clean up the preview URL
      setTimeout(() => {
        URL.revokeObjectURL(objectUrl);
      }, 1000);
    } catch (err) {
      console.error('Error uploading image:', err);
      setError(err instanceof Error ? err.message : 'Failed to upload image');
      // Revert to previous image if there was an error
      setPreviewUrl(currentImageUrl);
    } finally {
      setUploading(false);
    }
  }, [user, currentImageUrl, onImageUpdate]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp']
    },
    maxFiles: 1,
    disabled: uploading
  });

  const handleRemove = async () => {
    if (!user || !currentImageUrl) return;
    
    setUploading(true);
    setError(null);
    setSuccess(null);

    try {
      // Extract the file path from the URL
      const urlParts = currentImageUrl.split('/');
      const fileName = urlParts[urlParts.length - 1];
      const filePath = `${user.id}/${fileName}`;

      // Delete the file from storage
      const { error: deleteError } = await supabase.storage
        .from('profile-images')
        .remove([filePath]);

      if (deleteError) {
        console.warn('Error deleting image file:', deleteError);
        // Continue anyway to update the profile
      }

      // Update profile to remove image URL
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ 
          profile_image_url: null,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);

      if (updateError) {
        throw updateError;
      }

      // Call the callback with null
      onImageUpdate('');
      setPreviewUrl(null);
      setSuccess('Profile picture removed successfully');
    } catch (err) {
      console.error('Error removing image:', err);
      setError(err instanceof Error ? err.message : 'Failed to remove image');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="w-full">
      <div className="mb-4">
        <h3 className="text-lg font-medium text-gray-900 mb-2">Profile Picture</h3>
        <p className="text-sm text-gray-500">
          Upload a professional photo to help clients recognize you. Square images work best.
        </p>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg flex items-center">
          <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0" />
          <p>{error}</p>
        </div>
      )}

      {success && (
        <div className="mb-4 p-3 bg-green-50 text-green-700 rounded-lg flex items-center">
          <CheckCircle className="w-5 h-5 mr-2 flex-shrink-0" />
          <p>{success}</p>
        </div>
      )}

      <div className="flex flex-col md:flex-row items-center gap-6">
        {/* Current/Preview Image */}
        <div className="relative w-32 h-32 rounded-full overflow-hidden bg-gray-100 flex-shrink-0">
          {previewUrl ? (
            <>
              <img
                src={previewUrl}
                alt="Profile"
                className="w-full h-full object-contain"
              />
              {!uploading && (
                <button
                  onClick={handleRemove}
                  className="absolute top-0 right-0 bg-red-500 text-white p-1 rounded-full"
                  title="Remove image"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </>
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gray-200">
              <Camera className="w-12 h-12 text-gray-400" />
            </div>
          )}
          
          {uploading && (
            <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
              <Loader2 className="w-8 h-8 text-white animate-spin" />
            </div>
          )}
        </div>

        {/* Upload Area */}
        <div 
          {...getRootProps()} 
          className={`flex-1 border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
            isDragActive
              ? 'border-purple-500 bg-purple-50'
              : 'border-gray-300 hover:border-purple-500 hover:bg-purple-50'
          } ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          <input {...getInputProps()} />
          <Upload className="w-10 h-10 text-purple-500 mx-auto mb-2" />
          <p className="text-gray-600">
            {isDragActive
              ? "Drop your image here"
              : "Drag and drop your profile picture here, or click to select"}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            JPG, PNG or GIF • Max 5MB
          </p>
        </div>
      </div>
    </div>
  );
};

export default ProfilePictureUpload;